import { QueryClient } from "react-query";

import { ToastType, showToast } from "../../shared/Toast";
import { ScenarioStickListKey } from "../hooks/useScenarioSticks";
import type { UploadCsvSurveysResponse } from "../hooks/useUploadCsvSurveys";
import type { UploadLegacyCsvSurveysParams } from "../hooks/useUploadLegacyCsvSurveys";

const ZIP_MIME = "application/zip";
const CSV_MIME = "text/csv";

export interface ValidateFileResult {
  valid: boolean;
  message?: string;
}

export const validateZipFile = (file: File): ValidateFileResult => {
  const filename = file.name ?? "";
  const isZip = file.type === ZIP_MIME || filename.toLowerCase().endsWith(".zip");

  if (!isZip) {
    return {
      valid: false,
      message: `${filename} is not a .zip file`
    };
  }

  return { valid: true };
};

export const validateCsvFile = (file: File): ValidateFileResult => {
  const filename = file.name ?? "";
  const isCsv = file.type === CSV_MIME || filename.toLowerCase().endsWith(".csv");

  if (!isCsv) {
    return {
      valid: false,
      message: `${filename} is not a .csv file`
    };
  }

  if (file.size === 0) {
    return {
      valid: false,
      message: `${filename} is empty`
    };
  }

  return { valid: true };
};

// ─── Error message maps ───────────────────────────────────────────────────────

interface ShapefileErrorResponse {
  error: string;
  properties?: string[];
}

const extractShapefileErrorData = (error: unknown): ShapefileErrorResponse | null => {
  if (typeof error === "object" && error !== null) {
    const maybeResponse = (error as { response?: { data?: ShapefileErrorResponse } })
      .response;
    const data = maybeResponse?.data;

    if (
      data &&
      typeof data.error === "string" &&
      data.error === "id property missing" &&
      Array.isArray(data.properties)
    ) {
      return data;
    }
  }

  return null;
};

const extractUploadErrorMessage = (error: unknown): string | undefined => {
  if (typeof error === "object" && error !== null) {
    const maybeResponse = (error as { response?: { data?: { error?: string } } })
      .response;
    const apiMessage = maybeResponse?.data?.error;

    if (typeof apiMessage === "string" && apiMessage.trim().length > 0) {
      return apiMessage;
    }

    const errorMessage = (error as { message?: string }).message;

    if (typeof errorMessage === "string" && errorMessage.trim().length > 0) {
      return errorMessage;
    }
  }

  return undefined;
};

const extractApiErrorCode = (error: unknown): string | undefined => {
  if (typeof error === "object" && error !== null) {
    const maybeResponse = (error as { response?: { data?: { code?: string } } }).response;
    const code = maybeResponse?.data?.code;

    if (typeof code === "string" && code.trim().length > 0) {
      return code;
    }
  }

  return undefined;
};

/**
 * Human-readable messages for API error codes returned by the CSV survey
 * import endpoint. Covers both two-file and legacy single-file formats.
 * Codes are defined in the backend spec.
 */
const CSV_ERROR_CODE_MESSAGES: Readonly<Record<string, string>> = {
  // ── File & upload errors ──────────────────────────────────────────────────
  FILE_MISSING:
    "No valid combination of files was provided. Provide either both surface and stations files (two-file format) or a single file with UTM zone and datum (legacy format).",
  FILE_EMPTY: "One of the uploaded files is empty.",
  PAYLOAD_TOO_LARGE: "A file exceeds the maximum allowed size (50 MB).",
  CSV_PARSE_ERROR: "A file could not be parsed as valid CSV.",
  CSV_NO_HEADER: "No header row was found in the uploaded file.",
  CSV_NO_DATA_ROWS: "No valid wells could be constructed from the uploaded data.",

  // ── CRS errors (two-file format) ─────────────────────────────────────────
  CRS_MISSING:
    "The coordinate reference system (CRS) row is absent or empty in one of the files.",
  CRS_PARSE_ERROR:
    "The CRS string in the file preamble does not match the expected format (<Datum> / UTM zone <ZoneN>).",
  CRS_MISMATCH:
    "The two files carry different coordinate reference systems. Both files must share the same CRS.",

  // ── UTM zone & datum errors (both formats) ───────────────────────────────
  UTM_ZONE_MISSING: "The UTM zone is required but was not provided.",
  UTM_ZONE_INVALID_FORMAT:
    "The UTM zone is not in a recognised format (expected e.g. '10N' or '33S').",
  UTM_ZONE_OUT_OF_RANGE: "The UTM zone number must be between 1 and 60.",
  DATUM_MISSING: "The datum is required but was not provided.",
  DATUM_UNSUPPORTED: "The datum must be one of: NAD83, NAD27, WGS84.",
  DATUM_HEMISPHERE_UNSUPPORTED:
    "NAD83 and NAD27 are only defined for the northern hemisphere. Use WGS84 for southern hemisphere zones.",

  // ── Column mapping errors (legacy format) ────────────────────────────────
  COLUMN_MAPPING_INVALID_JSON: "The column mapping is not valid JSON.",
  COLUMN_MAPPING_NOT_OBJECT: "The column mapping must be a JSON object.",
  REQUIRED_COLUMN_NOT_MAPPED:
    "A required column is absent from the mapping and could not be auto-detected.",
  MAPPED_COLUMN_NOT_IN_CSV:
    "A CSV header referenced in the column mapping was not found in the file.",
  UNKNOWN_TARGET_COLUMN: "A mapping value is not a recognised canonical column key.",
  DEPTH_COLUMNS_MISSING:
    "The mapping must include either 'subsea', or both 'tvd' and 'kb'.",

  // ── Infrastructure errors ─────────────────────────────────────────────────
  DB_CONNECTION_FAILURE: "Unable to connect to the database. Please try again later.",
  DB_TIMEOUT: "The database write operation timed out. Please try again.",
  DB_WRITE_FAILURE: "An unexpected error occurred while saving the survey data.",
  DUPLICATE_KEY_CONFLICT:
    "A unique constraint conflict occurred during the save operation.",
  UNSUPPORTED_MEDIA_TYPE: "The request content type is not supported.",
  INTERNAL_SERVER_ERROR: "An unexpected server error occurred."
};

const resolveCsvErrorMessage = (code: string | undefined, fallback: string): string =>
  (code && CSV_ERROR_CODE_MESSAGES[code]) ?? fallback;

/**
 * Routes an error message to the caller-supplied callback when provided,
 * falling back to a toast notification for callers that have not opted in to
 * inline error display.
 */
const reportError = (
  message: string,
  onError: ((msg: string) => void) | undefined,
  toastContainerId: string | undefined
): void => {
  if (onError) {
    onError(message);
  } else if (toastContainerId) {
    showToast(toastContainerId, message, ToastType.Error, { duration: 0 });
  }
};

// ─── Survey upload (shapefile) ────────────────────────────────────────────────

export interface HandleSurveyUploadResult {
  success: boolean;
  errorMessage?: string;
  properties?: string[];
  needsIdProperty?: boolean;
}

export type SurveyUploadFn = (variables: {
  scenarioId: string;
  file: File;
  idProperty?: string;
}) => Promise<unknown>;

interface HandleSurveyUploadParams {
  file: File;
  scenarioId: string;
  upload: SurveyUploadFn;
  queryClient: QueryClient;
  toastContainerId?: string;
  /** When provided, errors are passed to this callback instead of shown as toasts. */
  onError?: (message: string) => void;
  idProperty?: string;
}

export const handleSurveyUpload = async ({
  file,
  scenarioId,
  upload,
  queryClient,
  toastContainerId,
  onError,
  idProperty
}: HandleSurveyUploadParams): Promise<HandleSurveyUploadResult> => {
  try {
    await upload({ scenarioId, file, idProperty });
    await queryClient.refetchQueries({
      queryKey: [ScenarioStickListKey, { $oid: scenarioId }],
      exact: true
    });

    if (toastContainerId) {
      showToast(toastContainerId, "File uploaded successfully.", ToastType.Success);
    }

    return { success: true };
  } catch (error) {
    const shapefileError = extractShapefileErrorData(error);

    if (
      shapefileError &&
      shapefileError.properties &&
      shapefileError.properties.length > 0
    ) {
      return {
        success: false,
        needsIdProperty: true,
        properties: shapefileError.properties,
        errorMessage: shapefileError.error
      };
    }

    const errorMessage = extractUploadErrorMessage(error) ?? "File upload failed.";

    reportError(errorMessage, onError, toastContainerId);

    return {
      success: false,
      errorMessage
    };
  }
};

// ─── ZIP / shapefile upload ───────────────────────────────────────────────────

interface HandleZipUploadParams {
  file: File;
  scenarioId?: string;
  upload: SurveyUploadFn;
  queryClient: QueryClient;
  toastContainerId?: string;
  /** When provided, errors are passed to this callback instead of shown as toasts. */
  onError?: (message: string) => void;
  onIdPropertyRequired?: (file: File, properties: string[]) => void;
  idProperty?: string;
}

export const handleZipUpload = async ({
  file,
  scenarioId,
  upload,
  queryClient,
  toastContainerId,
  onError,
  onIdPropertyRequired,
  idProperty
}: HandleZipUploadParams): Promise<boolean> => {
  const validation = validateZipFile(file);

  if (!validation.valid) {
    reportError(
      validation.message ?? "Invalid file uploaded.",
      onError,
      toastContainerId
    );
    return false;
  }

  if (!scenarioId) {
    reportError(
      "No active scenario selected to upload surveys for.",
      onError,
      toastContainerId
    );
    return false;
  }

  const result = await handleSurveyUpload({
    file,
    scenarioId,
    upload,
    queryClient,
    toastContainerId,
    onError,
    idProperty
  });

  if (result.needsIdProperty) {
    if (result.properties && onIdPropertyRequired) {
      onIdPropertyRequired(file, result.properties);
    }
    return false;
  }

  return result.success;
};

// ─── Two-file CSV survey upload ───────────────────────────────────────────────

export type TwoFileCsvUploadFn = (variables: {
  scenarioId: string;
  surfaceFile: File;
  stationsFile: File;
}) => Promise<UploadCsvSurveysResponse>;

interface HandleTwoFileCsvUploadParams {
  surfaceFile: File;
  stationsFile: File;
  scenarioId?: string;
  upload: TwoFileCsvUploadFn;
  queryClient: QueryClient;
  toastContainerId?: string;
  /** When provided, errors are passed to this callback instead of shown as toasts. */
  onError?: (message: string) => void;
}

export const handleTwoFileCsvUpload = async ({
  surfaceFile,
  stationsFile,
  scenarioId,
  upload,
  queryClient,
  toastContainerId,
  onError
}: HandleTwoFileCsvUploadParams): Promise<boolean> => {
  const surfaceValidation = validateCsvFile(surfaceFile);
  if (!surfaceValidation.valid) {
    reportError(
      surfaceValidation.message ?? "Invalid surface locations file.",
      onError,
      toastContainerId
    );
    return false;
  }

  const stationsValidation = validateCsvFile(stationsFile);
  if (!stationsValidation.valid) {
    reportError(
      stationsValidation.message ?? "Invalid survey stations file.",
      onError,
      toastContainerId
    );
    return false;
  }

  if (!scenarioId) {
    reportError(
      "No active scenario selected to upload surveys for.",
      onError,
      toastContainerId
    );
    return false;
  }

  try {
    const response = await upload({ scenarioId, surfaceFile, stationsFile });
    await queryClient.refetchQueries({
      queryKey: [ScenarioStickListKey, { $oid: scenarioId }],
      exact: true
    });

    if (response?.status === "partial") {
      const failedUwis = response.details
        .filter((d) => d.status === "failed")
        .map((d) => `${d.uwi}: ${d.error ?? "unknown error"}`)
        .join("; ");

      if (toastContainerId) {
        showToast(
          toastContainerId,
          typeof response.wellsSaved === "number" &&
            typeof response.wellsFailed === "number"
            ? `Upload partially succeeded. ${response.wellsSaved} saved, ${response.wellsFailed} failed. ${failedUwis}`
            : "Upload partially succeeded.",
          ToastType.Warning,
          { duration: 0 }
        );
      }
    } else if (toastContainerId) {
      showToast(toastContainerId, "Survey uploaded successfully.", ToastType.Success);
    }

    return true;
  } catch (error) {
    const apiError = extractApiErrorCode(error);
    const fallback = extractUploadErrorMessage(error) ?? "CSV upload failed.";
    const message = resolveCsvErrorMessage(apiError, fallback);

    reportError(message, onError, toastContainerId);
    return false;
  }
};

// ─── Legacy single-file CSV survey upload ─────────────────────────────────────

export type LegacyCsvUploadFn = (
  variables: UploadLegacyCsvSurveysParams
) => Promise<UploadCsvSurveysResponse>;

interface HandleLegacyCsvUploadParams {
  file: File;
  utmZone: string;
  datum: string;
  columnMapping?: Record<string, string>;
  scenarioId?: string;
  upload: LegacyCsvUploadFn;
  queryClient: QueryClient;
  toastContainerId?: string;
  /** When provided, errors are passed to this callback instead of shown as toasts. */
  onError?: (message: string) => void;
}

export const handleLegacyCsvUpload = async ({
  file,
  utmZone,
  datum,
  columnMapping,
  scenarioId,
  upload,
  queryClient,
  toastContainerId,
  onError
}: HandleLegacyCsvUploadParams): Promise<boolean> => {
  const validation = validateCsvFile(file);
  if (!validation.valid) {
    reportError(validation.message ?? "Invalid CSV file.", onError, toastContainerId);
    return false;
  }

  if (!scenarioId) {
    reportError(
      "No active scenario selected to upload surveys for.",
      onError,
      toastContainerId
    );
    return false;
  }

  try {
    const response = await upload({
      scenarioId,
      file,
      utmZone,
      datum,
      columnMapping
    });
    await queryClient.refetchQueries({
      queryKey: [ScenarioStickListKey, { $oid: scenarioId }],
      exact: true
    });

    if (response?.status === "partial") {
      const failedUwis = response.details
        .filter((d) => d.status === "failed")
        .map((d) => `${d.uwi}: ${d.error ?? "unknown error"}`)
        .join("; ");

      if (toastContainerId) {
        showToast(
          toastContainerId,
          typeof response.wellsSaved === "number" &&
            typeof response.wellsFailed === "number"
            ? `Upload partially succeeded. ${response.wellsSaved} saved, ${response.wellsFailed} failed. ${failedUwis}`
            : "Upload partially succeeded.",
          ToastType.Warning,
          { duration: 0 }
        );
      }
    } else if (toastContainerId) {
      showToast(toastContainerId, "Survey uploaded successfully.", ToastType.Success);
    }

    return true;
  } catch (error) {
    const apiError = extractApiErrorCode(error);
    const fallback = extractUploadErrorMessage(error) ?? "CSV upload failed.";
    const message = resolveCsvErrorMessage(apiError, fallback);

    reportError(message, onError, toastContainerId);
    return false;
  }
};
